import type { AuthService } from "@storytell/auth";
import type { DeviceService } from "@storytell/device";
import type { EnvironmentConfig } from "@storytell/environment";
import type { LocaleService } from "@storytell/locale";
import type { Logger } from "@storytell/logger";
import { Noop as NoopLogger } from "@storytell/logger";
import type { StreamingService } from "@storytell/streaming";
import type { AssetsService } from "~/stores/assets";
import type { BillingService } from "~/stores/billing";
import type { ChatService } from "~/stores/chat/chatService";
import type { ConceptsService } from "~/stores/concepts/conceptsService";
import type { GrantsService } from "~/stores/grants/grantsService";
import type { GuidesService } from "~/stores/guides/guidesService";
import type { KnowledgeUIService } from "~/stores/knowledge/knowledgeUIService";
import type { LabelsService } from "~/stores/labels";
import type { ProjectsService } from "~/stores/projects";
import type { RelsService } from "~/stores/rels/relsService";
import type { UIStore } from "~/stores/uiService";
import type { UserContextService } from "~/stores/userContext";
import type { ViewsService } from "~/stores/views";
import type { ChatTitleService } from "./services/chatTitleService";

// Wire provides access to dependencies, global state, and the ability to
// interact with domains/business-logic.
export interface Wire {
  auth: AuthService;
  grants: GrantsService;
  device: DeviceService;
  chat: ChatService;
  chatTitle: ChatTitleService;
  guides: GuidesService;
  stream: StreamingService;
  billing: BillingService;
  environment: EnvironmentConfig;
  locale: LocaleService;
  logger: Logger;
  ui: UIStore;
  knowledgeUi: KnowledgeUIService;
  projects: ProjectsService;
  userContext: UserContextService;
  labels: LabelsService;
  views: ViewsService;
  assets: AssetsService;
  concepts: ConceptsService;
  rels: RelsService;
}

/** defaultWire signals that the wire is not yet initialized. */
export const defaultWire: Wire = Object.freeze({
  auth: {} as AuthService,
  grants: {} as GrantsService,
  ui: {} as UIStore,
  chat: {} as ChatService,
  chatTitle: {} as ChatTitleService,
  billing: {} as BillingService,
  guides: {} as GuidesService,
  stream: {} as StreamingService,
  device: {} as DeviceService,
  knowledgeUi: {} as KnowledgeUIService,
  locale: {} as LocaleService,
  environment: {} as EnvironmentConfig,
  logger: new NoopLogger("_"),
  projects: {} as ProjectsService,
  userContext: {} as UserContextService,
  labels: {} as LabelsService,
  views: {} as ViewsService,
  assets: {} as AssetsService,
  concepts: {} as ConceptsService,
  rels: {} as RelsService,
});
